// src/hooks/useNotificationCounts.js
/**
 * Hook for real-time notification counts (pending absences and expenses)
 *
 * OPTIMIZATION: Previously used 30-second polling for expenses which caused
 * ~120-240 Firestore reads per hour per user. Now uses real-time listeners
 * for all data, reducing reads to just the initial fetch + changes.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

export const useNotificationCounts = (user) => {
  const [counts, setCounts] = useState({
    pendingAbsences: 0,
    pendingSpesen: 0,
  });
  const [loading, setLoading] = useState(false);

  // Use refs to track expense counts per company for real-time updates
  const spesenCountsRef = useRef({});

  useEffect(() => {
    if (!user?.id) {
      setCounts({
        pendingAbsences: 0,
        pendingSpesen: 0,
      });
      return;
    }

    setLoading(true);

    const adminCompanyIds = user.adminCompanyIds || [];

    if (adminCompanyIds.length === 0) {
      setCounts({ pendingAbsences: 0, pendingSpesen: 0 });
      setLoading(false);
      return;
    }

    const unsubscribeFunctions = [];

    // Reset spesen counts ref
    spesenCountsRef.current = {};

    // 1. Real-time listener for pending absences
    const pendingAbsencesQuery = query(
      collection(db, 'absences'),
      where('status', '==', 'pendent'),
      where('companyId', 'in', adminCompanyIds)
    );

    const unsubscribeAbsences = onSnapshot(
      pendingAbsencesQuery,
      (snapshot) => {
        setCounts(prev => ({ ...prev, pendingAbsences: snapshot.docs.length }));
      },
      (error) => {
        // Error handled silently
      }
    );
    unsubscribeFunctions.push(unsubscribeAbsences);

    // 2. Real-time listeners for expenses (one per company subcollection)
    // This replaces the expensive 30-second polling with efficient real-time listeners
    // Cost: 1 read per document initially + 1 read per document change
    // vs. Previous: N reads every 30 seconds = ~120-240 reads/hour
    const updateTotalSpesen = () => {
      const total = Object.values(spesenCountsRef.current).reduce((sum, count) => sum + count, 0);
      setCounts(prev => ({ ...prev, pendingSpesen: total }));
    };

    adminCompanyIds.forEach(companyId => {
      // Initialize count for this company
      spesenCountsRef.current[companyId] = 0;

      const spesenQuery = query(
        collection(db, `companies/${companyId}/expenses`),
        where('status', '==', 'pending')
      );

      const unsubscribeSpesen = onSnapshot(
        spesenQuery,
        (snapshot) => {
          spesenCountsRef.current[companyId] = snapshot.docs.length;
          updateTotalSpesen();
        },
        (error) => {
          // Error handled silently
        }
      );
      unsubscribeFunctions.push(unsubscribeSpesen);
    });

    setLoading(false);

    // Cleanup function
    return () => {
      unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, JSON.stringify(user?.adminCompanyIds)]); // Re-run if adminCompanyIds change

  const refreshCounts = useCallback(() => {
    // Counts are now updated automatically via real-time listeners
    // This function is kept for API compatibility but does nothing
  }, []);

  return {
    counts,
    loading,
    refreshCounts
  };
};